/* assets/section-statement-graphics.js */
(() => {
  if (customElements.get('stmt-product')) return;

  class StatementProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-stmt-variants]');
      if (!json) return;

      try {
        this.variants = JSON.parse(json.textContent);
      } catch {
        this.variants = [];
      }

      this.form       = this.querySelector('form');
      this.idInput    = this.querySelector('[data-stmt-id]');
      this.priceEl    = this.querySelector('[data-stmt-price]');
      this.compareEl  = this.querySelector('[data-stmt-compare]');
      this.badgeEl    = this.querySelector('[data-stmt-badge]');
      this.skuEl      = this.querySelector('[data-stmt-sku]');
      this.atc        = this.querySelector('[data-stmt-atc]');
      this.atcText    = this.querySelector('[data-stmt-atc-text]');
      this.errorEl    = this.querySelector('[data-stmt-error]');
      this.image      = this.querySelector('[data-stmt-image]');
      this.dimUnavailable = this.hasAttribute('data-dim-unavailable');
      this.swapImage      = this.hasAttribute('data-image-swap');

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.form?.addEventListener('submit', this.onSubmit.bind(this));

      this.refreshAvailability();
    }

    get selectedOptions() {
      const inputs = [...this.querySelectorAll('[data-stmt-option]')];
      const byIndex = [];

      inputs.forEach((el) => {
        const i = Number(el.dataset.optionIndex);
        if (el.tagName === 'SELECT') byIndex[i] = el.value;
        else if (el.checked) byIndex[i] = el.value;
      });

      return byIndex;
    }

    matchVariant(options) {
      return this.variants.find((v) =>
        options.every((val, i) => v.options[i] === val)
      );
    }

    onChange(event) {
      if (!event.target.matches('[data-stmt-option]')) return;

      const variant = this.matchVariant(this.selectedOptions);
      this.updateOptionLabels();
      this.refreshAvailability();
      this.updateVariant(variant);
    }

    onClick(event) {
      const btn = event.target.closest('[data-stmt-qty]');
      if (!btn) return;

      const input = this.querySelector('[data-stmt-qty-input]');
      if (!input) return;

      const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.stmtQty));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateOptionLabels() {
      this.selectedOptions.forEach((val, i) => {
        const el = this.querySelector(`[data-stmt-option-value="${i}"]`);
        if (el) el.textContent = val;
      });
    }

    refreshAvailability() {
      if (!this.dimUnavailable) return;

      const selected = this.selectedOptions;

      this.querySelectorAll('input[data-stmt-option]').forEach((input) => {
        const i = Number(input.dataset.optionIndex);
        const probe = [...selected];
        probe[i] = input.value;

        const match = this.variants.find((v) =>
          probe.every((val, idx) => idx === i ? v.options[idx] === val : (val === undefined || v.options[idx] === val))
        );

        const label = this.querySelector(`label[for="${input.id}"]`);
        if (label) label.dataset.unavailable = String(!match || !match.available);
      });
    }

    updateVariant(variant) {
      if (this.idInput) this.idInput.value = variant ? variant.id : '';

      if (this.atc) {
        const available = Boolean(variant && variant.available);
        this.atc.disabled = !available;
        if (this.atcText) {
          this.atcText.textContent = available
            ? this.atc.dataset.label
            : this.atc.dataset.soldOut;
        }
      }

      if (!variant) return;

      if (this.priceEl) this.priceEl.textContent = variant.price;

      if (this.compareEl) {
        this.compareEl.textContent = variant.compare_at_price || '';
        this.compareEl.hidden = !variant.compare_at_price;
      }

      if (this.badgeEl) {
        const tpl = this.badgeEl.dataset.badgeTemplate || '';
        this.badgeEl.textContent = variant.savings ? tpl.split('[savings]').join(variant.savings) : '';
        this.badgeEl.hidden = !variant.savings;
      }

      if (this.skuEl) {
        const span = this.skuEl.querySelector('span');
        if (span) span.textContent = variant.sku || '';
        this.skuEl.hidden = !variant.sku;
      }

      if (this.swapImage && this.image && variant.image && this.image.src !== variant.image) {
        this.image.dataset.loading = 'true';
        const preload = new Image();
        preload.onload = () => {
          this.image.src = variant.image;
          this.image.removeAttribute('srcset');
          this.image.dataset.loading = 'false';
        };
        preload.src = variant.image;
      }

      if (window.history && window.location.search.includes('variant=')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.atc || this.atc.disabled) return;

      this.setError('');
      this.atc.setAttribute('aria-busy', 'true');
      this.atc.disabled = true;

      try {
        const body = new FormData(this.form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart.');

        this.notifyCart(data);
      } catch (err) {
        this.setError(err.message);
      } finally {
        this.atc.removeAttribute('aria-busy');
        this.atc.disabled = false;
      }
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    notifyCart(item) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { item } }));
      document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: { item } }));
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:build', { bubbles: true, detail: { item } })
      );

      // Duck-typed drawer open — works across most OS 2.0 themes
      const drawer =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification') ||
        document.querySelector('#CartDrawer') ||
        document.querySelector('[data-cart-drawer]');

      if (drawer && typeof drawer.open === 'function') drawer.open();
      else if (drawer && typeof drawer.show === 'function') drawer.show();
      else if (drawer) drawer.classList.add('is-open', 'active', 'open');
    }
  }

  customElements.define('stmt-product', StatementProduct);

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('stmt-product').forEach((el) => el.connectedCallback());
  });
})();